import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock, ShieldCheck, ArrowRight, KeyRound } from "lucide-react";

/**
 * Full-page gated preview shown when a signed-out visitor lands on premium content.
 * Renders a soft-blurred teaser above and a strong sign-in CTA card below.
 */
const PremiumGate = ({ kind = "briefing", title, excerpt, image }) => {
  const { pathname } = useLocation();
  return (
    <>
      {/* Locked preview */}
      <section className="relative bg-neutral-50 py-16 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6">
          <div className="relative">
            {image && (
              <img
                src={image}
                alt=""
                className="w-full h-64 md:h-80 object-cover rounded-2xl blur-sm opacity-40"
              />
            )}
            <div className="mt-8 space-y-3 max-w-2xl">
              <div className="h-6 bg-neutral-200 rounded w-3/4 blur-[2px]" />
              <div className="h-4 bg-neutral-200 rounded w-full blur-[2px]" />
              <div className="h-4 bg-neutral-200 rounded w-11/12 blur-[2px]" />
              <div className="h-4 bg-neutral-200 rounded w-4/5 blur-[2px]" />
              <div className="h-4 bg-neutral-200 rounded w-3/4 blur-[2px]" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-neutral-50/40 via-neutral-50/70 to-neutral-50 pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Unlock card */}
      <section className="bg-neutral-50 pb-20">
        <div className="max-w-3xl mx-auto px-6 -mt-24 relative">
          <div className="bg-white rounded-3xl border border-neutral-100 shadow-2xl p-8 md:p-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-teal-500/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-teal-600" />
            </div>
            <p className="mt-4 text-xs font-bold text-teal-600 tracking-widest">MEMBERS-ONLY {kind.toUpperCase()}</p>
            <h2 className="mt-2 text-2xl md:text-3xl font-extrabold text-neutral-900">
              {title || `This ${kind} is available to Astoria members`}
            </h2>
            {excerpt && <p className="mt-3 text-neutral-600 max-w-xl mx-auto">{excerpt}</p>}

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl mx-auto text-sm text-neutral-700">
              <div className="flex items-center gap-2 bg-neutral-50 rounded-full px-3 py-2 border border-neutral-100">
                <ShieldCheck className="w-4 h-4 text-teal-500" /> Verified access
              </div>
              <div className="flex items-center gap-2 bg-neutral-50 rounded-full px-3 py-2 border border-neutral-100">
                <KeyRound className="w-4 h-4 text-teal-500" /> Free to join
              </div>
              <div className="flex items-center gap-2 bg-neutral-50 rounded-full px-3 py-2 border border-neutral-100">
                <ArrowRight className="w-4 h-4 text-teal-500" /> Instant unlock
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to={`/member-login?redirect=${encodeURIComponent(pathname)}`}
                className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-3 rounded-full transition-colors"
              >
                Sign in to unlock <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={`/member-login?tab=register&redirect=${encodeURIComponent(pathname)}`}
                className="inline-flex items-center gap-2 border border-neutral-200 hover:border-teal-500 text-neutral-800 hover:text-teal-600 font-semibold px-6 py-3 rounded-full transition-colors"
              >
                Create free account
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PremiumGate;
